// src/component/ui/skeletons/SharedFeedbackViewSkeleton.tsx

import React from 'react';

/**
 * Shimmer Component for skeleton loading effect
 */
const Shimmer = ({ className }: { className: string }) => (
  <div
    className={`animate-pulse ${className}`}
    style={{
      background:
        'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 2s ease-in-out infinite',
    }}
  />
);

/**
 * SharedFeedbackView Skeleton Loader
 * Displays a loading skeleton with shimmer effect while feedback data is being fetched
 */
const SharedFeedbackViewSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>

      {/* Header Skeleton */}
      <header className="bg-white px-6 pt-4 pb-4 border-b border-gray-100">
        {/* Back button skeleton */}
        <div className="flex items-center gap-2 mb-4">
          <Shimmer className="h-3 w-3 rounded" />
          <Shimmer className="h-3 w-12 rounded" />
        </div>
        
        {/* Title and avatar skeleton */}
        <div className="flex justify-between items-center">
          <div className="flex flex-col space-y-2 flex-1">
            <Shimmer className="h-8 w-64 rounded" />
          </div>
          <div className="flex items-center gap-3">
            <Shimmer className="h-10 w-10 rounded-full" />
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex h-[calc(100vh-110px)]">
        {/* Left Sidebar Skeleton */}
        <div className="w-[27%] min-w-[240px] max-w-[540px] bg-white">
          {/* Search and filter header skeleton */}
          <div className="pl-6 pr-2 mb-1 py-4">
            <div className="flex items-center justify-between">
              <Shimmer className="h-5 w-40 rounded" />
              <div className="flex items-center gap-2">
                <Shimmer className="h-8 w-8 rounded-lg" />
                <Shimmer className="h-8 w-8 rounded-lg" />
              </div>
            </div>
          </div>

          {/* Feedback List Skeleton */}
         <div
              className="mt-2 ml-4 mr-4 h-full rounded-[12px] bg-[#F7F7F7] overflow-y-auto custom-scrollbar-overlay"
              style={{ maxHeight: "calc(100vh - 150px)" }}
            >
              <div className="p-[10px]">
            
            {[1, 2, ].map((i) => (
              <div
                key={i}
                className="p-3 w-full h-28 3xl:h-32 mb-2 bg-white rounded-lg border border-gray-200"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar skeleton */}
                  <Shimmer className="w-8 h-8 rounded-full flex-shrink-0" />
                  
                  {/* Text content skeleton */}
                  <div className="flex-1 space-y-2">
                    <Shimmer className="h-4 w-full rounded" />
                    <Shimmer className="h-4 w-3/4 rounded" />
                  </div>
                </div>
                
                {/* Bottom row with index and icons */}
                <div className="mt-3 flex items-center justify-between">
                  <Shimmer className="h-3 w-8 rounded" />
                  <div className="flex gap-1">
                    <Shimmer className="h-5 w-5 rounded" />
                    <Shimmer className="h-5 w-5 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
</div>
        {/* Right Content Area Skeleton */}
        <div className="flex-1 mt-10 ml-2 mr-4 bg-white border border-[#C5D0F5] rounded-[10px] shadow-sm overflow-hidden">
          {/* Feedback Text Skeleton */}
          <div className="px-6 pt-4 bg-white space-y-2">
            <Shimmer className="h-4 w-full rounded" />
            <Shimmer className="h-4 w-5/6 rounded" />
          </div>

          {/* Preview Area Skeleton */}
          <div className="flex-1 px-6 py-4 flex items-center justify-center">
            <div className="w-full h-[500px] bg-gray-100 rounded-[14px] flex items-center justify-center">
              <div className="text-center">
                <Shimmer className="w-16 h-16 mx-auto mb-4 rounded-lg" />
                <Shimmer className="h-4 w-32 mx-auto rounded" />
              </div>
            </div>
          </div>

          {/* Bottom Controls Skeleton */}
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-4">
              {/* Acknowledge button skeleton */}
              <Shimmer className="w-40 2xl:w-44 3xl:w-48 4xl:w-52 h-10 xl:h-12 3xl:h-14 4xl:h-16 rounded-lg" />
              
              {/* Unclear button skeleton */}
              <Shimmer className="w-40 3xl:w-44 4xl:w-48 h-10 xl:h-12 3xl:h-14 4xl:h-16 rounded-lg" />
              
              {/* Separator */}
              <div className="w-[2px] h-12 bg-gray-200" />
              
              {/* Audio/video controls skeleton */}
              <Shimmer className="flex-1 h-10 xl:h-12 3xl:h-14 4xl:h-16 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedFeedbackViewSkeleton;